(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.ThornvellaRegional = factory();
})(typeof window === 'undefined' ? this : window, function () {
  'use strict';
  const eu = new Set('AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE'.split(' '));
  const marker = '_thornvella_regional_swap';
  const regionFor = country => eu.has(country) ? 'eu' : ['AU', 'NZ'].includes(country) ? 'au' : ['US', 'CA'].includes(country) ? 'us' : 'row';
  const normalize = value => String(value).normalize('NFKC').trim().toLowerCase();
  const optionName = name => normalize(name).replace('colour', 'color');
  const cleanProperties = properties => Object.fromEntries(Object.entries(properties || {}).filter(([key]) => key !== marker).sort(([a], [b]) => a.localeCompare(b)));
  const identity = item => JSON.stringify([String(item.variant_id || item.id), cleanProperties(item.properties), item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || null]);
  const quantityOf = (cart, source) => cart.items.filter(item => identity(item) === source.identity).reduce((sum, item) => sum + item.quantity, 0);
  const issue = (code, links = []) => Object.assign(new Error(code), { code, links });
  const expand = compact => {
    if (compact.version !== 3) return compact;
    const regions = ['au', 'us', 'eu', 'row'];
    const products = Object.fromEntries(compact.p.map(p => [p[0], { id: String(p[1]), family: p[2], kind: p[3] === 1 ? 'hoodie' : p[3] === 2 ? 'standard-tee' : 'shared', supplier: p[4], catalogs: regions.filter((_, i) => p[5] & (1 << i)), online: Boolean(p[6]), physical: Boolean(p[7]), profiles: p[8] }]));
    const families = Object.fromEntries(compact.f.map((family, i) => [i, Object.fromEntries(regions.map((region, j) => [region, family[j].map(id => compact.p[id][0])]))]));
    return { products, families, profiles: compact.profiles, routes: compact.routes || {} };
  };
  const targetsFor = (handle, index, country) => {
    const record = index.products[handle];
    if (!record) return [handle];
    const market = regionFor(country);
    const routes = index.routes?.[handle];
    if (routes && Object.hasOwn(routes, market)) {
      const target = routes[market], candidate = index.products[target];
      return candidate?.online && candidate.catalogs.includes(market) ? [target] : [];
    }
    if (record.kind === 'hoodie') {
      const supplier = country === 'AU' ? 'au' : 'us';
      if (record.supplier === supplier && record.online && record.catalogs.includes(market)) return [handle];
      return index.families[record.family][supplier] || [];
    }
    if (record.kind === 'standard-tee') return index.families[record.family][market === 'row' ? 'us' : market] || [];
    return record.online && record.catalogs.includes(market) ? [handle] : [];
  };
  const matchVariant = (item, product) => {
    const options = Object.fromEntries((item.options_with_values || []).map(option => [optionName(option.name), normalize(option.value)]));
    const names = product.options.map(option => optionName(option.name || option));
    if (Object.keys(options).length !== names.length || names.some(name => !(name in options))) return null;
    return product.variants.find(variant => names.every((name, i) => normalize(variant.options[i]) === options[name])) || null;
  };
  async function resolve(cart, index, country, loadProduct) {
    const products = new Map(), resolved = [];
    for (const item of cart.items) {
      const targets = targetsFor(item.handle, index, country);
      if (targets.length !== 1) throw issue(targets.length ? 'edition' : 'unavailable', targets.map(handle => ({ handle })));
      const handle = targets[0], record = index.products[handle];
      if (record?.physical && record.profiles.length && !record.profiles.some(id => {
        const profile = index.profiles[id];
        return profile && (profile.rest || profile.countries.includes(country));
      })) throw issue('destination', [{ handle }]);
      if (!products.has(handle)) products.set(handle, await loadProduct(handle));
      const product = products.get(handle);
      if (record && String(product.id) !== record.id) throw issue('unavailable', [{ handle }]);
      const variant = matchVariant(item, product);
      if (!variant?.available) throw issue('unavailable', [{ handle, variant: variant?.id }]);
      const sellingPlan = item.selling_plan_allocation?.selling_plan?.id || item.selling_plan;
      if (sellingPlan && String(variant.id) !== String(item.variant_id || item.id)) throw issue('unavailable', [{ handle, variant: variant.id }]);
      const target = { id: variant.id, quantity: item.quantity, properties: cleanProperties(item.properties) };
      if (sellingPlan) target.selling_plan = sellingPlan;
      resolved.push({ source: item, target });
    }
    return resolved;
  }
  async function plan(cart, index, country, loadProduct) {
    const sources = new Map(), additions = new Map();
    for (const { source: item, target: addition } of await resolve(cart, index, country, loadProduct)) {
      if (String(addition.id) === String(item.variant_id || item.id)) continue;
      const sourceId = identity(item), source = sources.get(sourceId) || { identity: sourceId, quantity: 0 };
      source.quantity += item.quantity;
      sources.set(sourceId, source);
      const key = identity(addition), previous = additions.get(key);
      if (previous) previous.quantity += addition.quantity;
      else additions.set(key, addition);
    }
    return { country, sources: [...sources.values()], additions: [...additions.values()] };
  }
  const markedItems = (cart, transaction) => cart.items.filter(item => item.properties?.[marker] === transaction.id);
  const completedProperties = item => ({ ...cleanProperties(item.properties), [marker]: 'complete' });
  const basketCounts = items => {
    const counts = new Map();
    for (const item of items) counts.set(identity(item), (counts.get(identity(item)) || 0) + item.quantity);
    return [...counts.entries()].sort(([a], [b]) => a.localeCompare(b));
  };
  const sameBasket = (a, b) => JSON.stringify(basketCounts(a.items || a)) === JSON.stringify(basketCounts(b.items || b));
  const targetsReady = (cart, transaction) => transaction.additions.every(target => markedItems(cart, transaction).filter(item => identity(item) === identity(target)).reduce((sum, item) => sum + item.quantity, 0) === target.quantity);
  const cleanedTargetsReady = (cart, transaction) => transaction.additions.every(target => cart.items.filter(item => identity(item) === identity(target)).reduce((sum, item) => sum + item.quantity, 0) >= target.quantity);
  async function finish(transaction, api, journal) {
    let cart = await api.cart();
    const counts = transaction.sources.map(source => quantityOf(cart, source));
    if (counts.every(count => count === 0) && cleanedTargetsReady(cart, transaction)) {
      for (const item of markedItems(cart, transaction)) await api.change(item.key, item.quantity, completedProperties(item));
      journal.clear();
      return api.cart();
    }
    if (!targetsReady(cart, transaction)) {
      if (transaction.sources.every((source, i) => counts[i] === source.quantity)) {
        for (const item of markedItems(cart, transaction)) await api.update({ [item.key]: 0 });
        cart = await api.cart();
        if (!markedItems(cart, transaction).length) { journal.clear(); throw issue('retry'); }
      }
      throw issue('pending');
    }
    if (!counts.every(count => count === 0)) {
      if (!transaction.sources.every((source, i) => counts[i] === source.quantity)) throw issue('changed');
      await api.guard();
      const updates = Object.fromEntries(cart.items.filter(item => transaction.sources.some(source => source.identity === identity(item))).map(item => [item.key, 0]));
      try { await api.update(updates); } catch (_) {}
      cart = await api.cart();
      if (!transaction.sources.every(source => quantityOf(cart, source) === 0) || !targetsReady(cart, transaction)) throw issue('pending');
    }
    for (const item of markedItems(cart, transaction)) await api.change(item.key, item.quantity, completedProperties(item));
    journal.clear();
    return api.cart();
  }
  async function replace(plan, api, journal, id) {
    if (!plan.additions.length) return { changed: false, cart: await api.cart() };
    await api.guard();
    const transaction = { ...plan, id };
    journal.set(transaction);
    try {
      await api.add(transaction.additions.map(item => ({ ...item, properties: { ...item.properties, [marker]: id } })));
    } catch (_) {
      const cart = await api.cart();
      if (!markedItems(cart, transaction).length && transaction.sources.every(source => quantityOf(cart, source) === source.quantity)) {
        journal.clear();
        throw issue('retry');
      }
    }
    return { changed: true, cart: await finish(transaction, api, journal) };
  }
  async function finishImport(transaction, api, journal) {
    let cart = await api.cart();
    const originals = () => cart.items.filter(item => item.properties?.[marker] !== transaction.id);
    if (transaction.phase === 'adding') {
      if (!targetsReady(cart, transaction)) {
        if (!sameBasket(originals(), transaction.originals)) throw issue('changed');
        for (const item of markedItems(cart, transaction)) await api.update({ [item.key]: 0 });
        cart = await api.cart();
        if (markedItems(cart, transaction).length) throw issue('pending');
        journal.clear();
        throw issue('retry');
      }
      if (!sameBasket(originals(), transaction.originals)) throw issue('changed');
      transaction.phase = 'removing';
      journal.set(transaction);
    }
    if (transaction.phase === 'removing') {
      if (!targetsReady(cart, transaction)) throw issue('pending');
      const allowed = new Map(basketCounts(transaction.originals));
      if (basketCounts(originals()).some(([key, quantity]) => quantity > (allowed.get(key) || 0))) throw issue('changed');
      await api.guard();
      const updates = Object.fromEntries(originals().map(item => [item.key, 0]));
      if (Object.keys(updates).length) {
        try { await api.update(updates); } catch (_) {}
      }
      cart = await api.cart();
      if (originals().length || !targetsReady(cart, transaction)) throw issue('pending');
      transaction.phase = 'cleaning';
      journal.set(transaction);
    }
    for (const item of markedItems(cart, transaction)) await api.change(item.key, item.quantity, completedProperties(item));
    cart = await api.cart();
    if (!sameBasket(cart, transaction.additions)) throw issue('changed');
    journal.clear();
    return cart;
  }
  async function importBasket(items, api, journal, id) {
    const aggregated = new Map();
    for (const item of items) {
      const key = identity(item), existing = aggregated.get(key);
      if (existing) existing.quantity += item.quantity;
      else aggregated.set(key, { ...item });
    }
    const additions = [...aggregated.values()], originalCart = await api.cart();
    if (sameBasket(originalCart, additions)) return originalCart;
    await api.guard();
    const transaction = { type: 'import', phase: 'adding', id, additions, originals: originalCart.items };
    journal.set(transaction);
    try { await api.add(additions.map(item => ({ ...item, properties: { ...item.properties, [marker]: id } }))); } catch (_) {}
    return finishImport(transaction, api, journal);
  }
  return { regionFor, targetsFor, matchVariant, resolve, plan, replace, finish, identity, marker, expand, sameBasket, importBasket, finishImport };
});

